import classNames from 'classnames';
import type { ReactNode } from 'react';
import Navbar from '../../../components/Navbar/Navbar.component';
import AvailabilityPrompt from 'src/components/AvailabilityPrompt/AvailabilityPrompt.component';
import InactiveUserBanner from 'src/components/InactiveUserBanner/InactiveUserBanner.component';
import { eAlertType } from '../../ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../ToastProvider/Toast.hook.ts';
import { tProvidersProps } from '../Layout.types';
import { useLayoutContext } from '../Layout.context.ts';
import { useIntegrationsContext } from '../../IntegrationsProvider/Integrations.context.ts';
import { useAlertCenterContext } from '../../AlertCenterProvider/AlertCenter.hook.ts';
import { eIntegrationStatus } from '../../IntegrationsProvider/Integrations.enums.ts';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import AlertBanner from 'src/components/AlertCenter/AlertBanner.component';
import AlertSurfaceBoundary from 'src/components/AlertCenter/AlertSurfaceBoundary.component';

/** The same measured app frame can host a local, source-free design fixture. */
export default function PrivateLayoutFrame(
  props: tProvidersProps & { sidebar: ReactNode },
) {
  const { children } = props;
  const { addAlert } = useToastContext();
  const { sidebarOpen } = useLayoutContext();
  const {
    whatsappIntegration,
    checkAndDeleteBannedAccount,
    setWhatsappIntegration,
  } = useIntegrationsContext();
  // RET-1879 — the retirement gate. Same expression the navbar reads: the
  // per-pageload flag snapshot OR an active dev override, never a raw flag.
  const { areSurfacesEnabled } = useAlertCenterContext();
  const { currentWorkspace } = useWorkspaceContext();

  function deleteBannedAccount() {
    checkAndDeleteBannedAccount().then(() => {
      setWhatsappIntegration({
        ...whatsappIntegration,
        state: eIntegrationStatus.NO_ACCOUNT,
      });
      addAlert({
        type: eAlertType.INFO,
        message: 'Listo! Puedes intentar conectar un nuevo número.',
      });
    });
  }

  return (
    <main className="LayoutPrivate">
      <Navbar />

      <div className="LayoutPrivate__Container">
        {/* sticky needs self-start: as a stretched flex item the nav spans
            the full content height and never has room to stick (AGI-193).
            z-30 because sticky ALWAYS creates a stacking context: without a
            positive z-index the sidebar's flyouts (workspace switcher, avatar
            menu) get trapped at effective z-0 and paint UNDER later-DOM page
            content. 30 matches the shell's dropdown tier (Navbar menus) and
            stays below overlays (z-40/50 modals and banners). */}
        <nav
          className={classNames(
            'sticky top-0 z-30 self-start transition-[width] duration-[250ms] ease-in-out',
            sidebarOpen ? 'w-[260px]' : 'w-[60px]',
          )}
        >
          {props.sidebar}
        </nav>
        <section className="page-section">
          {/* INT-1653 — the critical card: the single P0 winner of the window
              arbitration, at the top of the content column on every module
              (before the page's own title), no longer a full-width strip in
              the navbar. Keyed by workspace so a switch remounts it against
              the new snapshot — never a frame carrying the previous
              workspace's critical. */}
          {areSurfacesEnabled && (
            <AlertSurfaceBoundary surface="wide">
              <AlertBanner key={currentWorkspace?.id ?? 'no-workspace'} />
            </AlertSurfaceBoundary>
          )}
          {/* LeadAgentAcceptanceBannerContainer is mounted per-page (Dashboard,
              Pipe, AutomationSelection) so it renders below each surface's
              header section instead of at the top of the layout (RET-1230). */}
          <InactiveUserBanner />
          {/* Mounted beside the banner rather than in ModalsProvider: that
              context holds ~50 booleans and re-renders every consumer on any
              toggle, and this prompt fires from the chat send path (RET-1350). */}
          <AvailabilityPrompt />
          {/* RET-1879 — legacy retirement: the banned-number fact is one of the
              Alert Center's own alerts, so this hardcoded strip renders only
              while the center is off. <InactiveUserBanner /> above is NOT gated
              (R15: that in-page banner IS its alert's surface). */}
          {!areSurfacesEnabled &&
            whatsappIntegration.state === eIntegrationStatus.BANNED && (
              <div className="flex gap-2 w-[80rem] text-sm bg-yellow-100 border-yellow-200 border-2 rounded-md px-3 py-2 absolute z-50 top-14 text-yellow-500 ">
                ¡Oh no! Tu número ha sido bloqueado por WhatsApp. Puedes
                conectar un nuevo número y seguir en contacto con tus leads. ¡No
                pierdas ni una oportunidad!
                <button
                  className="text-yellow-600 font-bold"
                  onClick={deleteBannedAccount}
                >
                  Conectar nuevo número
                </button>
              </div>
            )}
          {children}
        </section>
      </div>
    </main>
  );
}
